import type { InsurancePlanEvidence, ValerieInsurancePlan } from "@/src/models/valerie-insurance-plan";

const GENERIC_PLAN_NAME_TOKENS = new Set([
  "benefit",
  "commercial",
  "coverage",
  "epo",
  "health",
  "hmo",
  "insurance",
  "plan",
  "pos",
  "ppo",
  "unknown",
]);

export function isMeaningfulInsurancePlanName(planName: string | null | undefined): boolean {
  return normalizeInsurancePlanIdentity(planName)
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .some((token) => !GENERIC_PLAN_NAME_TOKENS.has(token));
}

// Keep punctuation: collapsing it can conflate distinct products. Aliases must
// be curated in the catalog, never learned from an unverified document.
export function normalizeInsurancePlanIdentity(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

export function matchValerieInsurancePlan(
  plans: readonly ValerieInsurancePlan[],
  evidence: InsurancePlanEvidence
): ValerieInsurancePlan | null {
  const name = normalizeInsurancePlanIdentity(evidence.planName);
  const type = normalizeInsurancePlanIdentity(evidence.planType);
  const business = normalizeInsurancePlanIdentity(evidence.lineOfBusiness);

  if (!isMeaningfulInsurancePlanName(name)) return null;

  const matches = plans.filter((plan) => {
    const names = [plan.name, plan.payerReportedPlanName, ...plan.planNameAliases];

    return (
      names.some((candidate) => normalizeInsurancePlanIdentity(candidate) === name) &&
      (!type || normalizeInsurancePlanIdentity(plan.planType) === type) &&
      (!business || normalizeInsurancePlanIdentity(plan.lineOfBusiness) === business)
    );
  });

  return matches.length === 1 ? matches[0]! : null;
}
